import { Component, inject } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { DataDisplayComponent, ButtonComponent, DataContainerComponent } from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { UseCaseParticipationService } from '../../../../../services/use-case-participation/use-case-participation.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import {
  OrganizationDetails,
  AdmissionState,
  CommentType
} from '../../../../../services/organizations/organization.model';
import { UseCaseParticipationRequest, UseCaseParticipationComment } from '../../../../../services/use-case-participation/use-case-participation.model';
import {
  CommentsComponent,
  Comment
} from '../../../../../components/organisms/comments/comments.component';
import { ProtectedContentComponent } from '../../../../../components/organisms/protected-content/protected-content.component';
import { AdmissionDecisionComponent } from '../../../../../components/organisms/admission-decision/admission-decision.component';
import { NavigationService } from '@services/navigation.service';

@Component({
  selector: 'app-usecase-request-admission-page',
  imports: [
    AsyncPipe,
    CommonModule,
    DataDisplayComponent,
    DataContainerComponent,
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    CommentsComponent,
    ProtectedContentComponent,
    ButtonComponent,
    AdmissionDecisionComponent
  ],
  templateUrl: './usecase-request-admission-page.component.html',
  styleUrl: './usecase-request-admission-page.component.scss'
})
export class UsecaseRequestAdmissionPageComponent {
  private fb = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private useCaseParticipationService = inject(UseCaseParticipationService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);

  requiredRoles = this.navigationService.getAccessRoles('admin/requests');

  approvalForm = this.fb.group({
    reasoning: ['', Validators.required],
    comment: ['', Validators.required]
  });

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  private dataRefresh$ = new BehaviorSubject<void>(undefined);

  useCaseRequest$: Observable<UseCaseParticipationRequest> = combineLatest([
    this.id$,
    this.dataRefresh$
  ]).pipe(
    switchMap(([id]) => this.useCaseParticipationService.getDetailsById(id))
  );

  organizationData$: Observable<OrganizationDetails> = this.useCaseRequest$.pipe(
    switchMap((request) => this.organizationService.getDetailsById(request.organizationId))
  );

  admissionState$ = this.useCaseRequest$.pipe(map((data) => data.admissionState));

  private commentsRefresh$ = new BehaviorSubject<void>(undefined);
  private readonly commentsPageSize = 5;
  private commentsPage = 0;
  private allLoadedComments: UseCaseParticipationComment[] = [];

  private commentsState$ = new BehaviorSubject<{ items: UseCaseParticipationComment[]; hasMore: boolean }>({
    items: [],
    hasMore: false
  });

  private commentsLoader$ = combineLatest([this.id$, this.commentsRefresh$]).pipe(
    switchMap(([id]) => {
      this.commentsPage = 0;
      this.allLoadedComments = [];
      return this.useCaseParticipationService.getComments(id, { page: 0, size: this.commentsPageSize, sort: 'date,desc' }).pipe(
        catchError(() => of({ content: [] as UseCaseParticipationComment[], pagination: { last: true } } as any))
      );
    }),
    tap((result) => {
      this.allLoadedComments = result.content;
      this.commentsState$.next({ items: this.allLoadedComments, hasMore: !result.pagination.last });
    })
  ).subscribe();

  existingReasoning$ = combineLatest([this.id$, this.admissionState$, this.commentsRefresh$]).pipe(
    switchMap(([id, admissionState]) => {
      if (admissionState === AdmissionState.APPROVED || admissionState === AdmissionState.REJECTED) {
        return this.useCaseParticipationService.getComments(id, { page: 0, size: 1000, sort: 'date,desc' }).pipe(
          map((result) => {
            const targetType = admissionState === AdmissionState.APPROVED ? CommentType.APPROVAL : CommentType.REJECTION;
            const comment = result.content.find((c) => c.type === targetType);
            return comment?.text || 'Keine Begründung angegeben';
          }),
          catchError(() => of('Keine Begründung angegeben'))
        );
      }
      return of('');
    })
  );

  commentsForBox$: Observable<Comment[]> = this.commentsState$.pipe(
    map((state) =>
      state.items.map((c) => ({
        id: c.id,
        author: c.author,
        text: c.text,
        date: c.date,
        type: c.type || CommentType.COMMENT
      }))
    )
  );

  hasMoreComments$: Observable<boolean> = this.commentsState$.pipe(
    map((state) => state.hasMore)
  );

  useCaseDetails$ = this.useCaseRequest$.pipe(
    map((request) => {
      return [
        {
          label: this.translocoService.translate('useCaseParticipation.detail.useCaseName'),
          value: [request.useCaseName]
        },
        {
          label: this.translocoService.translate('useCaseParticipation.detail.requestDate'),
          value: [
            request.createdDate
              ? new Date(request.createdDate).toLocaleDateString('de-DE')
              : '-'
          ]
        }
      ];
    })
  );

  userDetails$ = this.organizationData$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('approval.summary.name.label'),
          value: [
            [
              dto.responsiblePerson.title || '',
              dto.responsiblePerson.firstName,
              dto.responsiblePerson.lastName
            ]
              .filter(Boolean)
              .join(' ')
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.contactData.phoneNumber.label'),
          value: [dto.responsiblePerson.phoneNumber]
        },
        {
          label: this.translocoService.translate('approval.summary.contactData.email.label'),
          value: [dto.responsiblePerson.email]
        },
        {
          label: this.translocoService.translate('approval.summary.position.label'),
          value: [dto.responsiblePerson.position]
        }
      ];
    })
  );

  organisationDetails$ = this.organizationData$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('approval.summary.legalName.label'),
          value: [dto.legalName + ' ' + dto.legalForm]
        },
        {
          label: this.translocoService.translate('approval.summary.foundingDate.label'),
          value: [dto.foundingDate ? new Date(dto.foundingDate).toLocaleDateString() : '']
        },
        {
          label: this.translocoService.translate('approval.summary.address.label'),
          value: [
            `${dto.organisationAddress.street} ${dto.organisationAddress.houseNumber}`,
            `${dto.organisationAddress.postalCode} ${dto.organisationAddress.city}`
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.website.label'),
          value: [dto.website]
        },
        {
          label: this.translocoService.translate('approval.summary.sector.label'),
          value: [dto.sector]
        },
        {
          label: this.translocoService.translate('approval.summary.description.label'),
          value: [dto.description]
        }
      ];
    })
  );

  backToList() {
    void this.router.navigate(['admin', 'requests']);
  }

  reject() {
    const reasoning = this.approvalForm.value.reasoning!;
    this.id$
      .pipe(
        switchMap((id) => this.useCaseParticipationService.reject(id, reasoning)),
        tap(() => {
          this.dataRefresh$.next();
          this.commentsRefresh$.next();
        }),
        catchError((error) => {
          console.error('Error rejecting request:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  approve() {
    const reasoning = this.approvalForm.value.reasoning!;
    this.id$
      .pipe(
        switchMap((id) => this.useCaseParticipationService.approve(id, reasoning)),
        tap(() => {
          this.dataRefresh$.next();
          this.commentsRefresh$.next();
        }),
        catchError((error) => {
          console.error('Error approving request:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  onCommentAdded(text: string) {
    this.id$
      .pipe(
        switchMap((id) => this.useCaseParticipationService.addComment(id, { text: text })),
        tap(() => this.commentsRefresh$.next()),
        catchError((error) => {
          console.error('Error adding comment:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  loadMoreComments() {
    this.commentsPage++;
    this.id$.pipe(
      take(1),
      switchMap((id) =>
        this.useCaseParticipationService.getComments(id, { page: this.commentsPage, size: this.commentsPageSize, sort: 'date,desc' })
      ),
      tap((result) => {
        this.allLoadedComments = [...this.allLoadedComments, ...result.content];
        this.commentsState$.next({ items: this.allLoadedComments, hasMore: !result.pagination.last });
      }),
      catchError(() => of(null))
    ).subscribe();
  }
}
